import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Poster } from './Poster';

/** Bentuk minimal satu slide — cocok untuk Drama maupun entri Update. */
export interface HeroItem {
  platform: string;
  id: string;
  title: string;
  cover: string | null;
  totalEpisodes: number | null;
  status?: string | null;
}

/** Jeda auto-swipe. */
const AUTO_MS = 5000;

/**
 * Ambang geser: seberapa jauh jari harus bergerak sebelum dianggap niat
 * pindah slide. Dihitung dari lebar layar (8%), dengan lantai 24px —
 * angka tetap 44px terasa jauh di ponsel sempit dan terlalu dekat di
 * layar lebar. Bagian penting: kalau gestur dipotong sistem (Telegram
 * iOS kerap membatalkan sentuhan mendatar), keputusan tetap dibuat dari
 * jarak terakhir yang sempat terbaca, bukan dibuang.
 */
const MIN_SWIPE_PX = 24;
const SWIPE_RATIO = 0.08;

/** Di atas ini, pointerup dianggap geser — klik ke halaman detail dibatalkan. */
const DRAG_SLOP = 8;

function Slide({ item }: { item: HeroItem }) {
  return (
    <div className="relative h-full w-full shrink-0">
      <div className="absolute inset-0">
        <Poster src={item.cover} title={item.title} />
      </div>
      {/* Gradasi supaya teks selalu terbaca di atas gambar apa pun. */}
      <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/80 to-transparent" />

      <div className="absolute inset-x-4 bottom-3.5 flex flex-col gap-2">
        <div className="flex gap-1.5">
          <span className="rounded-md bg-gold-grad px-2 py-1 text-[10px] font-extrabold text-gold-ink">
            Populer
          </span>
          {item.status && (
            <span className="rounded-md border border-brand-400/60 px-2 py-1 text-[10px] font-bold text-brand-400">
              {item.status}
            </span>
          )}
        </div>
        <h2 className="line-clamp-2 font-display text-2xl font-extrabold leading-tight drop-shadow-[0_2px_16px_rgba(0,0,0,0.6)]">
          {item.title}
        </h2>
        <p className="text-[11px] text-neutral-300">
          <span className="uppercase tracking-wide">{item.platform}</span>
          {item.totalEpisodes ? ` · ${item.totalEpisodes} episode` : ''}
          {' · Subtitle Indonesia'}
        </p>
        <span className="flex h-[42px] items-center justify-center gap-2 rounded-xl bg-gold-grad shadow-gold">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#14100A">
            <path d="M8 5.1v13.8c0 .8.9 1.3 1.6.9l10.7-6.9a1 1 0 0 0 0-1.7L9.6 4.2A1 1 0 0 0 8 5.1z" />
          </svg>
          <span className="font-display text-[13px] font-bold text-gold-ink">
            Tonton Sekarang
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * Carousel sorotan di beranda: berpindah sendiri tiap 5 detik dan bisa
 * digeser manual dengan jari/mouse.
 *
 * Catatan implementasi:
 *  - Pointer Events dipakai langsung (bukan library) supaya bundel tetap
 *    kecil; `touch-action: pan-y` menjaga scroll vertikal halaman tetap
 *    normal saat jari bergerak ke atas/bawah di atas carousel.
 *  - Seluruh slide dibungkus SATU <Link>, jadi geseran bisa membatalkan
 *    navigasi: kalau jari bergerak lebih dari DRAG_SLOP, klik yang lahir
 *    dari pointerup itu di-preventDefault.
 *  - Auto-swipe berhenti selama jari menyentuh, saat tab tidak terlihat,
 *    dan bila pengguna memilih "kurangi animasi" di sistemnya.
 */
export function HeroCarousel({ items }: { items: HeroItem[] }) {
  const [index, setIndex] = useState(0);
  const [dragPx, setDragPx] = useState(0);
  const [dragging, setDragging] = useState(false);
  // Dinaikkan tiap kali user menggeser → timer auto mulai lagi dari nol,
  // supaya slide tidak berpindah tepat sesudah dipilih manual.
  const [autoEpoch, setAutoEpoch] = useState(0);
  // Saat putaran kembali dari slide terakhir ke slide pertama, animasinya
  // dimatikan sesaat — kalau tidak, seluruh deret tergulung mundur di depan
  // mata alih-alih terasa berputar.
  const [animate, setAnimate] = useState(true);

  /*
   * Status "sedang digeser" disimpan di REF, bukan state: pointerdown →
   * pointermove → pointerup bisa tiba dalam satu tick sebelum React sempat
   * me-render ulang, dan handler yang membaca state lama akan mengabaikan
   * geseran itu — carousel pun tersangkut separuh jalan. State `dragging`
   * tetap ada, tapi hanya untuk mematikan transisi & jeda auto-swipe.
   */
  const draggingRef = useRef(false);
  const capturedRef = useRef(false);
  const pointerIdRef = useRef(0);
  const reanimateRef = useRef(0);
  const startXRef = useRef(0);
  const movedRef = useRef(0);
  const widthRef = useRef(1);
  const suppressClickRef = useRef(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const count = items.length;
  const safeIndex = count > 0 ? index % count : 0;

  /**
   * Lebar kotak carousel, dan TIDAK PERNAH nol.
   *
   * `clientWidth` bisa 0 saat elemen belum di-layout, saat tab baru
   * ditampilkan kembali, atau ketika induknya sedang berubah ukuran
   * (rotasi layar). Nol di situ fatal: geseran dibagi dengan lebar, jadi
   * 160px geseran pernah berubah menjadi -16000% dan seluruh deret slide
   * terlempar keluar layar.
   */
  const boxWidth = (): number => {
    const w = boxRef.current?.getBoundingClientRect().width ?? 0;
    if (w >= 1) return w;
    return window.innerWidth >= 1 ? window.innerWidth : 1;
  };

  // Daftar bisa berganti (ganti kategori) — jangan tertinggal di slide lama.
  useEffect(() => {
    setIndex(0);
  }, [count, items[0]?.id]);

  useEffect(() => {
    if (count <= 1 || dragging) return;
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const id = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      setIndex((i) => {
        const next = i + 1;
        if (next >= count) {
          setAnimate(false);
          // setTimeout, BUKAN requestAnimationFrame: rAF tidak pernah jalan
          // saat tab tersembunyi, dan animasi akan tersangkut mati untuk
          // selamanya begitu putaran kebetulan berbalik di keadaan itu.
          window.clearTimeout(reanimateRef.current);
          reanimateRef.current = window.setTimeout(() => setAnimate(true), 50);
          return 0;
        }
        return next;
      });
    }, AUTO_MS);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(reanimateRef.current);
    };
  }, [count, dragging, autoEpoch]);

  /*
   * Inti geseran dipisah dari jenis event-nya. Satu perangkat bisa
   * mengirim Pointer Events (Chrome, Safari 13+, WebView modern), satu
   * lagi hanya Touch Events (WebView Android lawas, iOS jadul). Dengan
   * memisahkan logika dari sumbernya, keduanya memakai jalur yang sama
   * persis — bukan dua implementasi yang berbeda nasib.
   */
  const begin = (x: number) => {
    if (count <= 1) return;
    widthRef.current = boxWidth();
    startXRef.current = x;
    movedRef.current = 0;
    draggingRef.current = true;
    // Bersihkan penanda dari interaksi SEBELUMNYA. Geseran sering tidak
    // melahirkan event click sama sekali, jadi penanda yang hanya dihapus
    // di dalam onClick bisa tertinggal dan menelan klik berikutnya.
    suppressClickRef.current = false;
    setDragging(true);
  };

  const move = (x: number) => {
    if (!draggingRef.current) return;
    const dx = x - startXRef.current;
    movedRef.current = dx;
    // Tepi pertama/terakhir diberi tahanan supaya terasa mentok, tidak
    // seperti slide kosong.
    const atEdge =
      (safeIndex === 0 && dx > 0) || (safeIndex === count - 1 && dx < 0);
    setDragPx(atEdge ? dx * 0.35 : dx);
  };

  const finish = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (capturedRef.current) {
      try {
        boxRef.current?.releasePointerCapture(pointerIdRef.current);
      } catch {
        // Sudah terlepas sendiri saat pointerup — tidak apa-apa.
      }
      capturedRef.current = false;
    }
    const dx = movedRef.current;
    const threshold = Math.max(MIN_SWIPE_PX, widthRef.current * SWIPE_RATIO);
    if (Math.abs(dx) > DRAG_SLOP) suppressClickRef.current = true;
    if (dx <= -threshold && safeIndex < count - 1) {
      setIndex(safeIndex + 1);
    } else if (dx >= threshold && safeIndex > 0) {
      setIndex(safeIndex - 1);
    }
    setDragPx(0);
    setDragging(false);
    setAutoEpoch((n) => n + 1);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    pointerIdRef.current = e.pointerId;
    begin(e.clientX);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - startXRef.current;
    /*
     * Pointer ditangkap hanya SETELAH jelas ini geseran, bukan sejak
     * pointerdown. Sebabnya: selama capture aktif, browser mengirim event
     * `click` ke elemen penangkap — bukan ke <a> di bawah jari — sehingga
     * sekali sentuh biasa tidak lagi membuka halaman judul. Setelah ambang
     * terlewati, capture justru dibutuhkan supaya gerakan yang keluar dari
     * kotak carousel tetap sampai ke sini.
     */
    if (!capturedRef.current && Math.abs(dx) > DRAG_SLOP) {
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        capturedRef.current = true;
      } catch {
        // Browser lawas tanpa pointer capture — geseran tetap jalan.
      }
    }
    move(e.clientX);
  };

  /*
   * Kursor keluar dari kotak SEBELUM capture terpasang (geseran masih di
   * bawah ambang): tanpa ini, tombol mouse yang dilepas di luar tidak
   * pernah sampai ke kita dan carousel tersangkut menempel di kursor.
   * Sesudah capture terpasang, pointerleave diabaikan — semua event
   * memang sudah dialihkan ke sini.
   */
  const onPointerLeave = () => {
    if (!capturedRef.current) finish();
  };

  /*
   * Jalur cadangan Touch Events, HANYA dipasang bila peramban tidak punya
   * Pointer Events. Kalau dipasang bersamaan, satu sentuhan akan terbaca
   * dua kali (peramban modern mengirim keduanya demi kompatibilitas).
   */
  const touchOnly = typeof window !== 'undefined' && !('PointerEvent' in window);
  const touchHandlers = touchOnly
    ? {
        onTouchStart: (e: React.TouchEvent) => {
          const t = e.touches[0];
          if (t) begin(t.clientX);
        },
        onTouchMove: (e: React.TouchEvent) => {
          const t = e.touches[0];
          if (t) move(t.clientX);
        },
        onTouchEnd: finish,
        onTouchCancel: finish,
      }
    : {};

  if (count === 0) return null;

  const offsetPct = -safeIndex * 100;
  // Dijepit ±100%: berapa pun kacaunya pengukuran lebar, slide tidak
  // pernah terlempar lebih jauh dari satu layar.
  const rawPct = (dragPx / (widthRef.current || 1)) * 100;
  const dragPct = Math.max(-100, Math.min(100, rawPct));

  return (
    /* select-none: tanpa itu, menggeser dengan MOUSE malah menyorot teks
       judul alih-alih memindahkan slide. */
    <div
      ref={boxRef}
      className="no-native-drag relative h-[194px] select-none overflow-hidden rounded-[18px] bg-ink-850"
      style={{ touchAction: 'pan-y' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onPointerLeave={onPointerLeave}
      {...touchHandlers}
    >
      <div
        className="flex h-full"
        style={{
          transform: `translate3d(${offsetPct + dragPct}%, 0, 0)`,
          transition:
            dragging || !animate ? 'none' : 'transform 420ms ease-out',
        }}
      >
        {items.map((item) => (
          <Link
            key={`${item.platform}-${item.id}`}
            to={`/drama/${item.platform}/${item.id}`}
            draggable={false}
            onClick={(e) => {
              // Klik yang lahir dari geseran: batalkan navigasinya.
              if (suppressClickRef.current) e.preventDefault();
            }}
            className="block h-full w-full shrink-0"
          >
            <Slide item={item} />
          </Link>
        ))}
      </div>

      {count > 1 && (
        /*
         * Titik indikator sekaligus TOMBOL. Gestur geser bisa dibajak
         * sistem (gestur kembali di tepi layar iOS, gestur Telegram),
         * jadi selalu ada cara memindah slide yang tidak bergantung pada
         * gestur sama sekali. Area sentuhnya dilebarkan lewat padding,
         * sementara titiknya tetap kecil.
         */
        <div className="absolute inset-x-0 bottom-0 flex justify-center gap-0.5">
          {items.map((item, i) => (
            <button
              key={`dot-${item.platform}-${item.id}`}
              type="button"
              aria-label={`Slide ${i + 1} dari ${count}`}
              aria-current={i === safeIndex}
              onClick={() => {
                setIndex(i);
                setAutoEpoch((n) => n + 1);
              }}
              className="px-1 py-2"
            >
              <span
                className={`block h-[3px] rounded-full transition-all ${
                  i === safeIndex ? 'w-5 bg-brand-500' : 'w-2 bg-white/40'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
